/**
 * Creating a project (§4.2).
 *
 * The client account is the reason this is a dialog and not a two-page flow.
 * `projects.client_account_id` is NOT NULL, so a project cannot exist without an
 * account — and in practice a new project is very often the first thing anyone
 * records about a new client. Sending the user away to create the account first,
 * then back to re-enter everything, is the dead end that stops people using a
 * system at all. So the account field is a select over what already exists, with
 * a quick-create alternative inline, and the server writes both in one
 * transaction.
 *
 * §0.3: navy header on the dialog, gold reserved for the navy surface only and
 * never placed on white.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  ApiFailure,
  CLIENT_ACCOUNT_TYPES,
  CREATABLE_STATUSES,
  WORKSTREAM_CODES,
  WORKSTREAM_LABELS,
  type ClientAccountOption,
  type ClientAccountType,
  type CreatableStatus,
  type CreateProjectBody,
  type PillarCode,
  type Project,
  type WorkstreamCode,
} from '../lib/api';
import { formatStatus } from '../lib/format';

interface Props {
  /** The pillars this session may create into — never wider than the grant. */
  pillars: PillarCode[];
  /** The active `?entity=` narrowing, preselected when there is one. */
  entityScope: PillarCode | null;
  onClose: () => void;
  onCreated: (project: Project) => void;
}

type AccountMode = 'existing' | 'new';

const FIELD =
  'w-full rounded border border-border bg-white px-3 py-2 text-body text-text-primary ' +
  'focus:border-royal focus:outline-none focus:ring-2 focus:ring-royal/30';
const LABEL = 'micro-label mb-1 block text-text-secondary';

/** The server's error codes, said in English. */
const MESSAGES: Record<string, string> = {
  project_code_taken: 'That project code is already in use. Every code is unique across the system.',
  client_account_not_found: 'That client account is no longer available. Reload and pick again.',
  pillar_scope_denied: 'You do not have access to that entity.',
  referenced_row_not_found: 'Something this project refers to no longer exists.',
  invalid_request: 'Some fields need attention.',
};

export function NewProjectDialog({ pillars, entityScope, onClose, onCreated }: Props): JSX.Element {
  const firstField = useRef<HTMLInputElement>(null);

  const [pillar, setPillar] = useState<PillarCode>(
    entityScope && pillars.includes(entityScope) ? entityScope : (pillars[0] ?? 'CLM'),
  );
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [workstream, setWorkstream] = useState<WorkstreamCode>('COM-REM');
  const [status, setStatus] = useState<CreatableStatus>('lead_opportunity');
  const [siteCity, setSiteCity] = useState('');
  const [siteState, setSiteState] = useState('');

  const [accountMode, setAccountMode] = useState<AccountMode>('existing');
  const [accounts, setAccounts] = useState<ClientAccountOption[] | null>(null);
  const [accountId, setAccountId] = useState('');
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountType, setNewAccountType] = useState<ClientAccountType>('commercial');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldIssues, setFieldIssues] = useState<Record<string, string>>({});

  useEffect(() => {
    firstField.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The account list is scoped to the chosen pillar, so switching entity
  // re-reads it rather than offering accounts from a pillar the project is not in.
  useEffect(() => {
    let live = true;
    setAccounts(null);
    api
      .clientAccounts(pillar)
      .then((res) => {
        if (!live) return;
        setAccounts(res.accounts);
        // An empty pillar has nothing to select, so the form opens on the path
        // that can actually be completed instead of on an empty dropdown.
        if (res.accounts.length === 0) setAccountMode('new');
      })
      .catch(() => {
        if (live) setAccounts([]);
      });
    return () => {
      live = false;
    };
  }, [pillar]);

  const accountsForPillar = useMemo(() => accounts ?? [], [accounts]);

  const canSubmit =
    code.trim().length > 0 &&
    name.trim().length > 0 &&
    (accountMode === 'existing' ? accountId.length > 0 : newAccountName.trim().length > 0) &&
    !submitting;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setFieldIssues({});

    const body: CreateProjectBody = {
      code: code.trim(),
      name: name.trim(),
      pillar,
      workstream,
      status,
      ...(siteCity.trim() ? { siteCity: siteCity.trim() } : {}),
      ...(siteState.trim() ? { siteState: siteState.trim() } : {}),
      ...(accountMode === 'existing'
        ? { clientAccountId: accountId }
        : { newClientAccount: { name: newAccountName.trim(), accountType: newAccountType } }),
    };

    try {
      const created = await api.createProject(body);
      onCreated(created.project);
    } catch (err) {
      if (err instanceof ApiFailure) {
        const body = err.body as { error?: string; issues?: { path: (string | number)[]; message: string }[] };
        const codeKey = body.error ?? '';
        setError(MESSAGES[codeKey] ?? 'The project could not be created.');
        if (body.issues) {
          const next: Record<string, string> = {};
          for (const issue of body.issues) {
            const key = String(issue.path[0] ?? '');
            if (key && !next[key]) next[key] = issue.message;
          }
          setFieldIssues(next);
        }
      } else {
        setError('The project could not be created.');
      }
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy/60 p-4 sm:items-center"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-project-title"
        className="w-full max-w-2xl overflow-hidden rounded-card border border-border bg-white shadow-lg"
      >
        <div className="flex items-baseline justify-between gap-4 bg-navy px-5 py-3">
          <h2 id="new-project-title" className="text-h3 font-semibold text-white">
            New project
          </h2>
          <span className="micro-label text-gold">{pillar}</span>
        </div>

        <form onSubmit={submit} className="grid gap-4 p-5">
          {error ? (
            <p role="alert" className="rounded-card border border-danger px-3 py-2 text-body text-danger">
              {error}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={LABEL} htmlFor="np-code">
                Project code
              </label>
              <input
                id="np-code"
                ref={firstField}
                className={`${FIELD} numeric`}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="CLM-0412"
                required
                aria-describedby={fieldIssues.code ? 'np-code-error' : undefined}
              />
              {fieldIssues.code ? (
                <p id="np-code-error" className="mt-1 text-micro text-danger">
                  {fieldIssues.code}
                </p>
              ) : null}
            </div>

            <div>
              <label className={LABEL} htmlFor="np-pillar">
                Entity
              </label>
              <select
                id="np-pillar"
                className={FIELD}
                value={pillar}
                onChange={(e) => {
                  setPillar(e.target.value as PillarCode);
                  setAccountId('');
                }}
              >
                {pillars.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL} htmlFor="np-name">
              Project name
            </label>
            <input
              id="np-name"
              className={FIELD}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Iberia Parish Library — Main Branch Renovation"
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={LABEL} htmlFor="np-workstream">
                Workstream
              </label>
              <select
                id="np-workstream"
                className={FIELD}
                value={workstream}
                onChange={(e) => setWorkstream(e.target.value as WorkstreamCode)}
              >
                {WORKSTREAM_CODES.map((w) => (
                  <option key={w} value={w}>
                    {WORKSTREAM_LABELS[w]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={LABEL} htmlFor="np-status">
                Starting status
              </label>
              <select
                id="np-status"
                className={FIELD}
                value={status}
                onChange={(e) => setStatus(e.target.value as CreatableStatus)}
              >
                {CREATABLE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatStatus(s)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <fieldset className="rounded border border-border p-4">
            <legend className="micro-label px-1 text-text-secondary">Client account</legend>

            <div className="mb-3 flex gap-4">
              <label className="flex items-center gap-2 text-body text-text-primary">
                <input
                  type="radio"
                  name="np-account-mode"
                  checked={accountMode === 'existing'}
                  onChange={() => setAccountMode('existing')}
                  disabled={accountsForPillar.length === 0}
                />
                Existing
              </label>
              <label className="flex items-center gap-2 text-body text-text-primary">
                <input
                  type="radio"
                  name="np-account-mode"
                  checked={accountMode === 'new'}
                  onChange={() => setAccountMode('new')}
                />
                Create new
              </label>
            </div>

            {accountMode === 'existing' ? (
              <div>
                <label className={LABEL} htmlFor="np-account">
                  Account
                </label>
                <select
                  id="np-account"
                  className={FIELD}
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  disabled={accounts === null}
                  required
                >
                  <option value="">
                    {accounts === null ? 'Loading accounts…' : 'Select an account'}
                  </option>
                  {accountsForPillar.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} — {a.accountType}
                    </option>
                  ))}
                </select>
                {accounts !== null && accountsForPillar.length === 0 ? (
                  <p className="mt-2 text-body text-text-secondary">
                    {pillar} has no client accounts yet. Create the first one here.
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={LABEL} htmlFor="np-new-account">
                    New account name
                  </label>
                  <input
                    id="np-new-account"
                    className={FIELD}
                    value={newAccountName}
                    onChange={(e) => setNewAccountName(e.target.value)}
                    placeholder="Iberia Parish Government"
                    required
                  />
                </div>
                <div>
                  <label className={LABEL} htmlFor="np-new-account-type">
                    Account type
                  </label>
                  <select
                    id="np-new-account-type"
                    className={FIELD}
                    value={newAccountType}
                    onChange={(e) => setNewAccountType(e.target.value as ClientAccountType)}
                  >
                    {CLIENT_ACCOUNT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-body text-text-secondary sm:col-span-2">
                  The account is created together with the project. If the project is rejected,
                  the account is not created either.
                </p>
              </div>
            )}
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={LABEL} htmlFor="np-city">
                Site city <span className="normal-case">(optional)</span>
              </label>
              <input
                id="np-city"
                className={FIELD}
                value={siteCity}
                onChange={(e) => setSiteCity(e.target.value)}
              />
            </div>
            <div>
              <label className={LABEL} htmlFor="np-state">
                Site state <span className="normal-case">(optional)</span>
              </label>
              <input
                id="np-state"
                className={FIELD}
                value={siteState}
                onChange={(e) => setSiteState(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-border pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border px-4 py-2 text-body text-text-secondary hover:bg-surface"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded bg-royal px-4 py-2 text-body font-semibold text-white hover:bg-navy disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
